import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  EmailData, ICompanyContacts,
  IContact, IContactDescription, IUser,
  LeadData, LeadDataObject,
  LeadDocumentsModel, LeadsDataContactObject,
  LeadsHistory,
} from '../../../../models/leads/leads.model';
import { ActionHelper } from '../../../../helper/action.helper';
import { MatDialog } from '@angular/material/dialog';
import { PhoneNumberHelper } from '../../../../helper/phone-number.helper';
import { HideLoader, ShowLoader } from '../../../store/loader/loader.actions';
import { Store } from '@ngxs/store';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import { DateTimeHelper } from '../../../../helper/date-time.helper';
import { LOCALE_KEY } from '../../../../services/user-service/user-token.service';
import { AddressHelper } from '../../../../helper/address.helper';
import { ResourceCountry } from "../../../../models/resources/resources.model";
import { InfoSideBarEmailService } from "./info-side-bar-email.service";
import { LeadHelper } from '../../../../helper/lead.helper';
import { ContactHelper } from '../../../../helper/contact.helper';
import { Router } from '@angular/router';
import { UserProfileShareService } from '../../../../services/shared-service/user-profile-share.service';
import { CONTACTS_HAS_ADMIN_RIGHTS } from '../../../../constants/constants';
import {TitleHelper} from '../../../../helper/title.helper';

@Component({
  selector: 'fi-info-side-bar',
  templateUrl: './info-side-bar.component.html',
  styleUrls: ['./info-side-bar.component.scss'],
  standalone: false,
})
export class InfoSideBarComponent implements OnInit, OnChanges {

  @Input() sideBarActive: boolean;
  @Input() showDetails: boolean;
  @Input() contactToDisplay: IContact;
  @Input() user: IUser;
  @Input() isDuplicateLead: boolean;
  @Input() documents: LeadDocumentsModel[];
  @Input() selectedLeadId: number;
  @Input() selectedLeadDetails: LeadData;
  @Input() selectedLeadHistory: LeadsHistory[];
  @Input() loadNewLeadEmailData: boolean;
  @Input() selectedTabIndex: number;
  @Input() canEdit: boolean;
  @Input() userId: number;
  @Input() compendiumVisible: boolean;
  @Input() showAddEnquiry: boolean;
  @Input() leadCompendiumHasAccess: boolean | undefined;

  leadMap = new Map<number, LeadData>();
  hasNoEmail = false;
  hasNoPhone = false;
  hasNoMobile = false;
  hasNoAddress = false;
  dateTimeHelper = DateTimeHelper;
  expandForMobile = false;
  canEditContact = false;

  readonly leadTypeRelationshipManagement = LeadHelper.LEAD_TYPE_PLANER_RELATIONSHIP;


  @Output() onGetMainLead = new EventEmitter();
  @Output() onNewLeadCreate = new EventEmitter<LeadsDataContactObject>();
  @Output() onTabChanged = new EventEmitter<number>();
  @Output() onBrokeredTabSelected = new EventEmitter<LeadDataObject>();
  @Output() scrollToHistoryItem = new EventEmitter<string>();
  @Input() openedLeads: LeadDataObject[];
  @Input() connectedLeads: LeadDataObject[];
  @Input() companyContacts?: ICompanyContacts[] = [];
  @Input() closedLeads: LeadDataObject[];
  @Input() brokeredLeads: LeadDataObject[] = [];
  @Input() brokeringSourceLead: LeadDataObject | null = null;
  @Input() brokeringParentLeadId: number | null = null;
  @Input() showLoadClosedLeads: boolean;
  @Input() linkedContacts?: IContactDescription[];
  @Input() selectedLinkedContactId?: number;


  contactAdditionalEmailData: Promise<string>;
  contactEmailData: Promise<string>;
  nextToLastLeadIndex = 0;
  private countries: ResourceCountry[];
  private externalSavedData = new Map<string, EmailData | null>();
  private externalSavedDataPromise = new Map<string, Promise<EmailData | null>>();
  private transationsSubscription;

  constructor(
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    private readonly sharedProfileService: UserProfileShareService,
    private dialog: MatDialog,
    private store: Store,
    private router: Router,
    private infoSideBarEmailService: InfoSideBarEmailService,
  ) {
  }

  ngOnInit(): void {
    this.transationsSubscription = setInterval(() => {
      if ( !InfoSideBarEmailService.translationsDownloaded && !this.countries?.length ) {
        return
      }
      this.hasNoInformation();
      if ( (this.selectedLeadDetails && (( !this.contactAdditionalEmailData || !this.contactEmailData) || this.loadNewLeadEmailData)) ) {
        AddressHelper.getCountries().then(countryData => {
          this.countries = countryData;
          this.updateEmailData();
          clearInterval(this.transationsSubscription)
        });
      }
    }, 2000)
    this.filterCompanyContacts();
    this.updateCanEditContact();
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( changes && changes.companyContacts ) {
      this.filterCompanyContacts()
    }

    if ( changes && changes.contactToDisplay && changes.selectedLeadDetails && changes.selectedLeadDetails.previousValue
      && this.countries && this.selectedLeadDetails ) {
      this.externalSavedData.clear();
      this.externalSavedDataPromise.clear();
      this.updateEmailData();
    }
    this.updateCanEditContact();
  }


  * getEnquiries(): Iterable<LeadDataObject | { tabIndex: number }> {
    let tabIndex = 0;
    let lastTabIndex = 0

    if ( this.openedLeads ) {
      lastTabIndex += this.openedLeads.length;

      for ( const lead of this.openedLeads ) {
        (lead as any).style = this.setLeadOrderStyle(lead);
        (lead as any).class = 'is-opened';
        (lead as any).tabIndex = tabIndex++;
        yield lead;
      }
    }

    if ( this.connectedLeads ) {
      lastTabIndex += this.connectedLeads.length;

      for ( const lead of this.connectedLeads ) {
        (lead as any).style = this.setGroupOrderStyle(lead, InfoSideBarComponent.CONNECTED_LEADS_ORDER);
        (lead as any).class = 'is-connected';
        (lead as any).tabIndex = tabIndex++;
        yield lead;
      }
    }

    if ( this.closedLeads && !this.showLoadClosedLeads ) {
      lastTabIndex += this.closedLeads.length;

      for ( const lead of this.closedLeads ) {
        (lead as any).style = this.setGroupOrderStyle(lead, InfoSideBarComponent.CLOSED_LEADS_ORDER);
        (lead as any).class = 'is-closed';
        (lead as any).tabIndex = tabIndex++;
        yield lead;
      }
    }

    if ( lastTabIndex !== this.nextToLastLeadIndex ) {
      this.nextToLastLeadIndex = lastTabIndex;
    }
  }

  backToOverview(): void {
    sessionStorage.setItem('fromStartPage', 'false');
    setTimeout(() => {
      history.back();
    })
  }

  openMapNewTab(): void {
    const addressUrl = AddressHelper.getMapUrl(this.contactToDisplay);
    window.open(addressUrl);
  }

  updateEmailData(): void {
    this.contactEmailData = this.assembleContactEmail(this.contactToDisplay?.Email);
    this.contactAdditionalEmailData = this.assembleContactEmail(this.contactToDisplay?.AdditionalEmail);
  }

  editIconClick(): void {
    const name = 'EDIT_CONTENT';
    const dialogData = {
      id: this.contactToDisplay?.Id,
      email: this.contactToDisplay?.Email,
      compendiumHasAccess: this.leadCompendiumHasAccess === true,
      compendiumActionEnabled: this.checkIfCompendiumCheckboxEnabled(),
      compendiumActionVisible: this.compendiumVisible,
    };

    ActionHelper.showAction(name, this.dialog, dialogData)?.afterClosed().subscribe(data => {
      if ( data === 'update' ) {
        this.onGetMainLead.emit();
      }
    });
  }

  get contactHasInfo(): boolean {
    if ( this.contactToDisplay ) {
      return !!this.contactToDisplay.Phone ||
        !!this.contactToDisplay.Mobile ||
        !!this.contactToDisplay.Email && !this.contactToDisplay.Email.endsWith('@temp.finstral.com') ||
        !!this.contactToDisplay.AdditionalEmail && !this.contactToDisplay.AdditionalEmail.endsWith('@temp.finstral.com');
    }

    return false;
  }

  getItemText = ( lead: LeadDataObject ): string => {
    return lead.LeadTypeId === this.leadTypeRelationshipManagement ? lead.LeadTypeText : lead.DateFormattedId
  }

  hasNoInformation(): void {
    if ( !(this.contactToDisplay?.Address) && !(this.contactToDisplay?.ZipCode) && !(this.contactToDisplay?.CountryText) && !(this.contactToDisplay?.City) ) {
      this.hasNoAddress = true;
    } else {
      this.hasNoAddress = false;
    }

    if ( !this.contactToDisplay?.Email || this.contactToDisplay?.Email.endsWith('@temp.finstral.com') ) {
      this.hasNoEmail = true;
    } else {
      this.hasNoEmail = false;
    }
  }

  getEmailData( kkz: string ): Promise<EmailData> {
    return new Promise(resolve => {
      this.leadsDataService.getEmailData(kkz).subscribe(( { success, data } ) => {
        if ( success ) {
          return resolve(data)
        }
        return resolve(null);
      })
    })
  }

  private updateCanEditContact(): void {
    const isUserEditor = this.userId === this.selectedLeadDetails?.Editor?.Id;
    const isAdminUser = this.sharedProfileService.hasRight(CONTACTS_HAS_ADMIN_RIGHTS);

    if (isAdminUser) {
      this.canEditContact = !this.isDuplicateLead && !this.contactToDisplay?.Anonymized;
      return;
    }

    this.canEditContact = !this.isDuplicateLead
      && !this.contactToDisplay?.Anonymized
      && this.canEdit
      && isUserEditor;
  }


  private checkIfCompendiumCheckboxEnabled(): boolean {
    const actionExists = this.selectedLeadDetails.Actions.find(
      // the requirements in this case just check if action exists and ignore hidden flag
      ( action ) => (action.Name === 'COMPENDIUM_ALLOW' || action.Name === 'COMPENDIUM_DENY')
    );
    return !!actionExists;
  }

  private filterCompanyContacts(): void {
    this.companyContacts = this.companyContacts?.filter(contact => contact.Email !== this.contactToDisplay?.Email);
  }

  private async assembleContactEmail( email: string ): Promise<string> {
    const selectedLeadDetails = this.selectedLeadDetails;
    const lead = selectedLeadDetails?.Lead;
    const keywords = selectedLeadDetails?.Keywords ?? [];
    const hasB2BKeyword = !!keywords.find((it) => it.Text.toLowerCase() === 'b2b');
    const ExternalEmail = lead?.ExternalEmail;
    const studios = (selectedLeadDetails?.Editor?.Studios ?? [])
      .map(studio => studio?.trim())
      .filter(( studio ): studio is string => !!studio);
    const contactLanguage = selectedLeadDetails.Contact?.LanguageCode ?? this.getLanguage();
    this.infoSideBarEmailService.selectedLeadDetails = selectedLeadDetails;
    this.infoSideBarEmailService.countries = this.countries;

    if ( !studios.length ) {
      return this.infoSideBarEmailService.emailExternalDataByLanguageCode(contactLanguage, email, lead, ExternalEmail, hasB2BKeyword, null);
    }

    const externalData = await Promise.all(studios.map(studio => this.getOrLoadEmailData(studio)));


    return this.infoSideBarEmailService.emailExternalDataByLanguageCode(contactLanguage, email, lead, ExternalEmail, hasB2BKeyword, externalData);
  }

  private getOrLoadEmailData( kkz: string ): Promise<EmailData | null> {
    if ( this.externalSavedData.has(kkz) ) {
      return Promise.resolve(this.externalSavedData.get(kkz)!);
    }

    if ( !this.externalSavedDataPromise.has(kkz) ) {
      this.externalSavedDataPromise.set(kkz, this.getEmailData(kkz).then(( data: EmailData | null ) => {
        this.externalSavedData.set(kkz, data);
        return data;
      }).finally(() => {
        this.externalSavedDataPromise.delete(kkz);
      }));
    }

    return this.externalSavedDataPromise.get(kkz)!;
  }


  get contactPhone(): string {
    const phone = PhoneNumberHelper
      .getPhoneNumber(this.contactToDisplay?.PhonePrefix, this.contactToDisplay?.Phone);
    if ( phone === null ) {
      this.hasNoPhone = true;
    } else {
      this.hasNoPhone = false;
    }
    return phone;
  }

  get contactMobile(): string {
    const mobile = PhoneNumberHelper
      .getPhoneNumber(this.contactToDisplay?.MobilePrefix, this.contactToDisplay?.Mobile);
    if ( mobile === null ) {
      this.hasNoMobile = true;
    } else {
      this.hasNoMobile = false;
    }
    return mobile;
  }

  get contactAdditionalMobile(): string {
    return PhoneNumberHelper
      .getPhoneNumber(this.contactToDisplay?.PhoneOtherPrefix, this.contactToDisplay?.PhoneOther);
  }

  private async getDocuments( leadId: number ): Promise<LeadDocumentsModel[]> {
    return new Promise(( resolve, reject ) => {
      this.leadsDataService.getLeadDocuments(leadId).subscribe(( { success, data } ) => {
        if ( success ) {
          this.documents = data.filter(document => !document.Deleted);
          resolve(this.documents);
        } else {
          reject('no data');
        }
      });
    });
  }

  async onAttachmentsPopupOpen(): Promise<void> {
    this.store.dispatch(new ShowLoader(2000));
    new Promise(( resolve, reject ) => {
      this.getDocuments(this.selectedLeadId).then(( data ) => {
        if ( data ) {
          const ids =
            data.filter(item => !this.leadMap.has(item.LeadId)).map(item => item.LeadId);

          Promise.allSettled([...new Set(ids)].map(id => this.leadsDataService.getLeadById(id).toPromise())).then(( leads ) => {
            leads.forEach(( lead ) => {
              if ( lead.status === 'fulfilled' && lead.value?.data ) {
                this.leadMap.set(lead.value.data.Lead.Id, lead.value.data);
              }
            });
            resolve(data);
          }).catch(() => {
            reject('no data');
          })
        } else {
          reject('no data');
        }
      });
    }).then(() => {
      this.store.dispatch(new HideLoader());
      const actionData = {
        documents: this.documents,
        leadMap: this.leadMap,
        leadId: this.selectedLeadId,
      };
      ActionHelper.showAction('FI-ATTACHMENTS', this.dialog, actionData);
    });
  }

  getNameLiterals( name: string ): string {
    if ( name ) {
      const splittedName = name.trim().split(/\s+/gm);
      return splittedName.map(c => c[0]).join('').toUpperCase();
    }

    return '';
  }

  get leadForwarder(): LeadsHistory {
    return this.selectedLeadHistory?.find(item => item.ActionName === 'LEAD_FORWARD');
  }

  scrollToHistoryStart( id = 'firstHistoryComment' ): void {
    this.scrollToHistoryItem.emit(id);
  }

  getLanguage(): string {
    return sessionStorage.getItem(LOCALE_KEY);
  }

  getWebsiteUrl( website: string ): string {
    if ( website.includes('http') ) {
      return website;
    } else if ( website.includes('www.') ) {
      return 'http://' + website;
    } else {
      return 'http://www.' + website;
    }
  }

  openLeadDetail( contact: ICompanyContacts, newTab = false ): void {
    if ( contact.ManagementLeadId ) {
      this.redirectToDetailPage(contact.ManagementLeadId, newTab);
    } else {
      this.getLastLeadAndOpenDetail(contact.Id, newTab);
    }
  }

  addAdditionalContact(): void {
    const name = 'EDIT_CONTENT';
    const dialogData = {
      additionalContact: true,
      id: this.contactToDisplay?.Id,
      email: this.contactToDisplay?.Email,
    };

    ActionHelper.showAction(name, this.dialog, dialogData)?.afterClosed().subscribe(data => {
      if ( data === 'update' ) {
        this.onGetMainLead.emit();
      }
    });
  }

  private getLastLeadAndOpenDetail( contactId: number, newTab: boolean ): void {
    this.store.dispatch(new ShowLoader());
    this.leadsDataService.getLeadWithContactById(contactId).subscribe(( { success, data } ) => {
      this.store.dispatch(new HideLoader());
      if ( success && data.length ) {
        const sortedData = data.sort(( a, b ) => new Date(b.Lead.CreTS).getTime() - new Date(a.Lead.CreTS).getTime());
        const lastLead = sortedData[0]
        if ( lastLead ) {
          this.redirectToDetailPage(lastLead.Lead.Id, newTab);
        }
      }
    })
  }

  private redirectToDetailPage( leadId: number, newTab: boolean ): void {
    if ( newTab ) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/details/' + leadId])
      );

      window.open(url, '_blank');
    } else {
      this.router.navigate(['/details/' + leadId]);
    }
  }

  trackByLeadId( _index: number, lead: LeadDataObject ): number {
    return lead.Id;
  }

  private static readonly CONNECTED_LEADS_ORDER = 10000;
  private static readonly CLOSED_LEADS_ORDER = 20000;

  setLeadOrderStyle( lead: LeadDataObject ): string {
    if ( lead.LeadTypeId === this.leadTypeRelationshipManagement ) {
      return 'order: 1';
    }

    return `order: ${2 + this.getOpenGroupRank(lead)}`;
  }

  private setGroupOrderStyle( lead: LeadDataObject, baseOrder: number ): string {
    return `order: ${lead.LeadTypeId === this.leadTypeRelationshipManagement ? 1 : baseOrder}`;
  }

  private getOpenGroupRank( lead: LeadDataObject ): number {
    const merged = [
      ...(this.openedLeads ?? []),
      ...(this.brokeredLeads ?? []),
      ...(this.brokeringSourceLead ? [this.brokeringSourceLead] : []),
    ]
      .filter(item => item.LeadTypeId !== this.leadTypeRelationshipManagement)
      .sort((a, b) => new Date(b.CreTS).getTime() - new Date(a.CreTS).getTime());

    return Math.max(merged.findIndex(item => item.Id === lead.Id), 0);
  }

  protected readonly ContactHelper = ContactHelper;
  protected readonly titleHelper = TitleHelper;
  protected readonly leadHelper = LeadHelper;
}
